import React, { useEffect, useState } from 'react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Search, Filter, Download, Edit, Trash2, Eye, FileText, Calendar, Plus, Library, RefreshCw, CheckCircle, XCircle, Clock, Database, Globe, Info, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { dataSyncEvents, useDataSync } from '@/utils/dataSyncEvents';
import { supabase } from '@/integrations/supabase/client';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface DataPoint {
  id: string;
  name: string;
  custom_value: number;
  unit: string;
  scope?: string;
  reporting_period?: string;
  data_source?: string;
  validation_status?: string;
  created_at: string;
  custom_metadata?: Record<string, any>;
  category?: string;
  subcategory?: string;
  data_type: 'metric' | 'material';
}

interface DataTableProps {
  onTabChange?: (tab: string) => void;
}

export const DataTable = ({ onTabChange }: DataTableProps) => {
  const [data, setData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDataType, setFilterDataType] = useState('all');
  const [selectedDataPoint, setSelectedDataPoint] = useState<DataPoint | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState({
    metric_value: '',
    reporting_period: '',
    scope: '',
    data_source: '',
    notes: ''
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdminStatus, setCheckingAdminStatus] = useState(true);
  const [userType, setUserType] = useState<'individual' | 'organization_admin' | 'super_admin' | null>(null);

const { user } = useAuth();
  
  // ── Use organizationId everywhere instead of user.id ──────────────────
  // Admin:       organizationId === user.id
  // Team member: organizationId === admin's user.id (their org's identity)
  const { organizationId, loading: orgLoading } = useOrganization();
  
  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();

  useEffect(() => {
    if (user && organizationId && !orgLoading && !dataLoaded) {
      fetchData();
      checkAdminStatus();
    }
  }, [user, organizationId, orgLoading, dataLoaded]);

  // Subscribe to data changes from other components
  useDataSync(organizationId || '', () => {
    if (user) {
      fetchData();
    }
  });

  const checkAdminStatus = async () => {
    if (!user) return;

    try {
      setCheckingAdminStatus(true);

      // Check if user is a super admin
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('role, is_super_admin')
        .eq('user_id', user.id)
        .single();

      if (!adminError && adminUser && (adminUser.is_super_admin || adminUser.role === 'admin')) {
        setIsAdmin(true);
        setUserType('super_admin');
        return;
      }

      // Check if user is an organization admin
      const { data: orgMember, error: orgError } = await supabase
        .from('organization_members')
        .select('role, can_approve_data')
        .eq('user_id', user.id)
        .single();

      if (!orgError && orgMember && (orgMember.role === 'admin' || orgMember.can_approve_data)) {
        setIsAdmin(true);
        setUserType('organization_admin');
        return;
      }

      // Check if user is an individual user
      const { data: individualUser, error: individualError } = await supabase
        .from('individual_users')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!individualError && individualUser) {
        setIsAdmin(false);
        setUserType('individual');
        return;
      }

      setIsAdmin(false);
      setUserType(null);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setUserType(null);
    } finally {
      setCheckingAdminStatus(false);
    }
  };

const fetchData = async () => {
    if (!user || !organizationId) return;

    try {
      setLoading(true);

      // Load user metrics from Supabase
      const { data: metricsData, error: metricsError } = await supabase
        .from('user_metrics_data')
        .select('*')
        .eq('organization_id', organizationId)   // ← was user.id

        .order('created_at', { ascending: false });

      if (metricsError) {
        console.warn('Failed to load metrics:', metricsError);
      }

      // Load user materials from Supabase
      const { data: materialsData, error: materialsError } = await supabase
        .from('user_materials_data')
        .select('*')
        .eq('organization_id', organizationId)   // ← was user.id
        .order('created_at', { ascending: false });

      if (materialsError) {
        console.warn('Failed to load materials:', materialsError);
      }

      // Transform metrics data to match DataPoint interface
      const transformedMetrics: DataPoint[] = (metricsData || []).map(metric => ({
        id: metric.id,
        name: metric.name,
        custom_value: metric.custom_value || 0,
        unit: metric.unit || '',
        scope: metric.custom_metadata?.scope,
        reporting_period: metric.custom_metadata?.reporting_period,
        data_source: metric.custom_metadata?.data_source,
        validation_status: metric.custom_metadata?.validation_status || 'pending',
        created_at: metric.created_at,
        custom_metadata: metric.custom_metadata,
        category: metric.category,
        subcategory: metric.subcategory,
        data_type: 'metric' as const
      }));

      // Transform materials data to match DataPoint interface
      const transformedMaterials: DataPoint[] = (materialsData || []).map(material => ({
        id: material.id,
        name: material.name,
        custom_value: material.custom_density || 0,
        unit: material.unit || '',
        scope: material.custom_metadata?.scope,
        reporting_period: material.custom_metadata?.reporting_period,
        data_source: material.custom_metadata?.data_source,
        validation_status: material.custom_metadata?.validation_status || 'pending',
        created_at: material.created_at,
        custom_metadata: material.custom_metadata,
        category: material.category,
        subcategory: material.subcategory,
        data_type: 'material' as const
      }));

      const combinedData = [...transformedMetrics, ...transformedMaterials];
      console.log('Combined data for display:', combinedData.length, combinedData);
      setData(combinedData);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organizational data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'validated': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'flagged': return 'bg-red-100 text-red-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'Scope 1': return 'bg-red-100 text-red-800';
      case 'Scope 2': return 'bg-orange-100 text-orange-800';
      case 'Scope 3': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewData = (dataPoint: DataPoint) => {
    setSelectedDataPoint(dataPoint);
    setIsViewModalOpen(true);
  };

  const handleEditData = (dataPoint: DataPoint) => {
    setSelectedDataPoint(dataPoint);
    setEditFormData({
      metric_value: dataPoint.custom_value?.toString() || '',
      reporting_period: dataPoint.reporting_period || '',
      scope: dataPoint.scope || '',
      data_source: dataPoint.data_source || '',
      notes: dataPoint.custom_metadata?.notes || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedDataPoint || !user || !organizationId) return;

    try {
      // Prepare updates based on data type
      let updates: any = {};
      if (selectedDataPoint.data_type === 'metric') {
        updates.custom_value = parseFloat(editFormData.metric_value) || 0;
      } else {
        updates.custom_density = parseFloat(editFormData.metric_value) || 0;
      }

      updates.custom_metadata = {
        ...selectedDataPoint.custom_metadata,
        notes: editFormData.notes || null,
        reporting_period: editFormData.reporting_period || null,
        scope: editFormData.scope || null,
        data_source: editFormData.data_source || null,
        updated_at: new Date().toISOString()
      };

      // Update in Supabase based on data type
      let result;
      if (selectedDataPoint.data_type === 'metric') {
        result = await supabase
          .from('user_metrics_data')
          .update(updates)
          .eq('id', selectedDataPoint.id)
          .eq('organization_id', organizationId);   // ← was user.id
      } else {
        result = await supabase
          .from('user_materials_data')
          .update(updates)
          .eq('id', selectedDataPoint.id)
          .eq('organization_id', organizationId);   // ← was user.id
      }

      if (result.error) {
        throw result.error;
      }

      // Emit event to notify other components
      if (selectedDataPoint.data_type === 'metric') {
        dataSyncEvents.emitMetricChange('updated', selectedDataPoint.id, organizationId);
      } else {
        dataSyncEvents.emitMaterialChange('updated', selectedDataPoint.id, organizationId);
      }

      // Update local state
      const updatedData = data.map(item =>
        item.id === selectedDataPoint.id
          ? {
            ...item,
            custom_value: parseFloat(editFormData.metric_value) || 0,
            scope: editFormData.scope || item.scope,
            reporting_period: editFormData.reporting_period || item.reporting_period,
            data_source: editFormData.data_source || item.data_source,
            custom_metadata: {
              ...item.custom_metadata,
              notes: editFormData.notes || null,
              updated_at: new Date().toISOString()
            }
          }
          : item
      );
      setData(updatedData);

      toast({
        title: 'Success',
        description: 'Data point updated successfully.',
      });

      setIsEditModalOpen(false);
      setSelectedDataPoint(null);
    } catch (error) {
      console.error('Error updating data point:', error);
      toast({
        title: 'Error',
        description: 'Failed to update data point.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteData = (id: string) => {
    setIdToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!user || !organizationId || !idToDelete) return;

    try {
      // Find the data point to determine its type
      const dataPoint = data.find(item => item.id === idToDelete);
      if (!dataPoint) {
        throw new Error('Data point not found');
      }

      // Delete from Supabase based on data type
      let deleteResult;
      if (dataPoint.data_type === 'metric') {
        deleteResult = await supabase
          .from('user_metrics_data')
          .delete()
          .eq('id', idToDelete)
          .eq('organization_id', organizationId);   // ← was user.id
      } else {
        deleteResult = await supabase
          .from('user_materials_data')
          .delete()
          .eq('id', idToDelete)
          .eq('organization_id', organizationId);   // ← was user.id
      }

      if (deleteResult.error) {
        throw deleteResult.error;
      }

      // Emit event to notify other components
      if (dataPoint.data_type === 'metric') {
        dataSyncEvents.emitMetricChange('deleted', idToDelete, organizationId);
      } else {
        dataSyncEvents.emitMaterialChange('deleted', idToDelete, organizationId);
      }


      // Remove the data point from local state immediately
      const updatedData = data.filter(item => item.id !== idToDelete);
      setData(updatedData);

      toast({
        title: "Data Deleted",
        description: "Data point has been deleted successfully."
      });

      // Close dialog and reset state
      setIsDeleteDialogOpen(false);
      setIdToDelete(null);
    } catch (error) {
      console.error('Error deleting data:', error);
      toast({
        title: "Error",
        description: `Failed to delete data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
      // Close dialog on error too
      setIsDeleteDialogOpen(false);
      setIdToDelete(null);
    }
  };

  const handleApproveData = async (dataPoint: DataPoint) => {
    if (!user || !organizationId || !isAdmin) return;

    try {
      // Prepare updates
      const updates = {
        custom_metadata: {
          ...dataPoint.custom_metadata,
          validation_status: 'approved',
          approved_by: user.id,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      };

      // Update in Supabase based on data type
      let result;
      if (dataPoint.data_type === 'metric') {
        result = await supabase
          .from('user_metrics_data')
          .update(updates)
          .eq('id', dataPoint.id)
          .eq('organization_id', organizationId);   // ← was user.id
      } else {
        result = await supabase
          .from('user_materials_data')
          .update(updates)
          .eq('id', dataPoint.id)
          .eq('organization_id', organizationId);   // ← was user.id
      }

      if (result.error) {
        throw result.error;
      }

      // Update local state
      const updatedData = data.map(item =>
        item.id === dataPoint.id
          ? {
            ...item,
            validation_status: 'approved',
            custom_metadata: {
              ...item.custom_metadata,
              validation_status: 'approved',
              approved_by: user.id,
              approved_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          }
          : item
      );
      setData(updatedData);

      // Emit event for background sync
      if (dataPoint.data_type === 'metric') {
        dataSyncEvents.emitMetricChange('updated', dataPoint.id, organizationId);
      } else {
        dataSyncEvents.emitMaterialChange('updated', dataPoint.id, organizationId);
      }

      toast({
        title: 'Data Approved',
        description: `${dataPoint.name} has been approved successfully.`,
      });
    } catch (error) {
      console.error('Error approving data:', error);
      toast({
        title: 'Approval Failed',
        description: 'Failed to approve the data. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleRejectData = async (dataPoint: DataPoint) => {
    if (!user || !organizationId || !isAdmin) return;

    try {
      // Prepare updates
      const updates = {
        custom_metadata: {
          ...dataPoint.custom_metadata,
          validation_status: 'rejected',
          rejected_by: user.id,
          rejected_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      };

      // Update in Supabase based on data type
      let result;
      if (dataPoint.data_type === 'metric') {
        result = await supabase
          .from('user_metrics_data')
          .update(updates)
          .eq('id', dataPoint.id)
          .eq('organization_id', organizationId);   // ← was user.id
      } else {
        result = await supabase
          .from('user_materials_data')
          .update(updates)
          .eq('id', dataPoint.id)
          .eq('organization_id', organizationId);   // ← was user.id
      }

      if (result.error) {
        throw result.error;
      }

      // Update local state
      const updatedData = data.map(item =>
        item.id === dataPoint.id
          ? {
            ...item,
            validation_status: 'rejected',
            custom_metadata: {
              ...item.custom_metadata,
              validation_status: 'rejected',
              rejected_by: user.id,
              rejected_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          }
          : item
      );
      setData(updatedData);

      // Emit event for background sync
      if (dataPoint.data_type === 'metric') {
        dataSyncEvents.emitMetricChange('updated', dataPoint.id, organizationId);
      } else {
        dataSyncEvents.emitMaterialChange('updated', dataPoint.id, organizationId);
      }

      toast({
        title: 'Data Rejected',
        description: `${dataPoint.name} has been rejected.`,
      });
    } catch (error) {
      console.error('Error rejecting data:', error);
      toast({
        title: 'Rejection Failed',
        description: 'Failed to reject the data. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleManualRefresh = async () => {
    if (!user || !organizationId) return;

    try {
      setLoading(true);

      // Reload data from Supabase
      await fetchData();

      toast({
        title: 'Data Refreshed',
        description: 'Data has been refreshed successfully.',
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh data.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!(await guardDownload())) return;

    try {
      // Create CSV content
      const headers = [
        'Type',
        'Name',
        'Category',
        'Subcategory',
        'Value',
        'Unit',
        'Scope',
        'Status',
        'Data Source',
        'Reporting Period',
        'Description',
        'Notes',
        'Created At',
        'Updated At'
      ];

      const csvContent = [
        headers.join(','),
        ...data.map(item => [
          `"${item.data_type || ''}"`,
          `"${item.name || ''}"`,
          `"${item.category || ''}"`,
          `"${item.subcategory || ''}"`,
          `"${item.custom_value || ''}"`,
          `"${item.unit || ''}"`,
          `"${item.scope || ''}"`,
          `"${item.validation_status || ''}"`,
          `"${item.data_source || ''}"`,
          `"${item.reporting_period || ''}"`,
          `"${item.description || ''}"`,
          `"${item.custom_metadata?.notes || ''}"`,
          `"${item.created_at || ''}"`,
          `"${item.updated_at || ''}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `organizational_data_overview_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Export Successful',
        description: 'Organizational data has been exported to CSV successfully.',
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export organizational data to CSV.',
        variant: 'destructive'
      });
    }
  };

  const handleExportHTML = async () => {
    if (!(await guardDownload())) return;

    try {
      // Calculate metrics and materials counts
      const metrics = data.filter(item => item.data_type === 'metric');
      const materials = data.filter(item => item.data_type === 'material');

      // Generate AI insights based on the current filtered data
      const generateAIInsights = () => {
        if (data.length === 0) {
          return `
            <div class="insight-section">
              <h3>📊 Dataset Overview</h3>
              <div class="insight-card">
                <p>No organizational data available for analysis. Consider adding ESG metrics and materials data to begin tracking your sustainability performance.</p>
              </div>
            </div>
          `;
        }

        const insights = [];

        // Basic statistics
        const totalItems = data.length;
        const categories = [...new Set(data.map(item => item.category).filter(Boolean))];
        const scopes = [...new Set(data.map(item => item.scope).filter(Boolean))];
        const statuses = [...new Set(data.map(item => item.validation_status).filter(Boolean))];

        // Calculate additional metrics
        const avgValue = data.reduce((sum, item) => sum + (item.custom_value || 0), 0) / data.length;
        const recentItems = data.filter(item => {
          const createdDate = new Date(item.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return createdDate > thirtyDaysAgo;
        });

        // Dataset Overview Section
        insights.push(`
          <div class="insight-section">
            <h3>📊 Dataset Overview</h3>
            <div class="insight-card">
              <div class="metric-grid">
                <div class="metric-item">
                  <div class="metric-value">${totalItems}</div>
                  <div class="metric-label">Total Items</div>
                </div>
                <div class="metric-item">
                  <div class="metric-value">${metrics.length}</div>
                  <div class="metric-label">ESG Metrics</div>
                </div>
                <div class="metric-item">
                  <div class="metric-value">${materials.length}</div>
                  <div class="metric-label">Materials</div>
                </div>
                <div class="metric-item">
                  <div class="metric-value">${categories.length}</div>
                  <div class="metric-label">Categories</div>
                </div>
              </div>
              <p>This comprehensive dataset spans <strong>${scopes.length}</strong> emission scopes with <strong>${statuses.length}</strong> different validation statuses, providing a holistic view of your organization's sustainability performance.</p>
            </div>
          </div>
        `);

        // Data Type Distribution Section
        insights.push(`
          <div class="insight-section">
            <h3>📈 Data Type Distribution</h3>
            <div class="insight-card">
              <div class="distribution-chart">
                <div class="chart-bar">
                  <div class="bar-label">ESG Metrics</div>
                  <div class="bar-container">
                    <div class="bar-fill" style="width: ${Math.round(metrics.length / totalItems * 100)}%"></div>
                  </div>
                  <div class="bar-value">${metrics.length} (${Math.round(metrics.length / totalItems * 100)}%)</div>
                </div>
                <div class="chart-bar">
                  <div class="bar-label">Materials</div>
                  <div class="bar-container">
                    <div class="bar-fill" style="width: ${Math.round(materials.length / totalItems * 100)}%"></div>
                  </div>
                  <div class="bar-value">${materials.length} (${Math.round(materials.length / totalItems * 100)}%)</div>
                </div>
              </div>
              <p>The dataset contains <strong>${metrics.length}</strong> ESG metrics and <strong>${materials.length}</strong> materials, providing balanced coverage of both performance indicators and supply chain data.</p>
            </div>
          </div>
        `);

        // Category Analysis Section
        if (categories.length > 0) {
          const categoryCounts = categories.map(cat => ({
            category: cat,
            count: data.filter(item => item.category === cat).length,
            percentage: Math.round(data.filter(item => item.category === cat).length / totalItems * 100)
          })).sort((a, b) => b.count - a.count);

          insights.push(`
            <div class="insight-section">
              <h3>🏷️ Category Analysis</h3>
              <div class="insight-card">
                <div class="category-table">
                  <div class="table-header">
                    <div class="header-cell">Category</div>
                    <div class="header-cell">Count</div>
                    <div class="header-cell">Percentage</div>
                  </div>
                  ${categoryCounts.map(cat => `
                    <div class="table-row">
                      <div class="table-cell">${cat.category}</div>
                      <div class="table-cell">${cat.count}</div>
                      <div class="table-cell">${cat.percentage}%</div>
                    </div>
                  `).join('')}
                </div>
                <p>The most represented category is <strong>${categoryCounts[0].category}</strong> with ${categoryCounts[0].count} items (${categoryCounts[0].percentage}% of total). This indicates a strong focus on ${categoryCounts[0].category.toLowerCase()} in your sustainability strategy.</p>
              </div>
            </div>
          `);
        }

        // Scope Analysis Section
        if (scopes.length > 0) {
          const scopeCounts = scopes.map(scope => ({
            scope,
            count: data.filter(item => item.scope === scope).length,
            percentage: Math.round(data.filter(item => item.scope === scope).length / totalItems * 100)
          })).sort((a, b) => b.count - a.count);

          insights.push(`
            <div class="insight-section">
              <h3>🌍 Emission Scope Coverage</h3>
              <div class="insight-card">
                <div class="scope-grid">
                  ${scopeCounts.map(scope => `
                    <div class="scope-item">
                      <div class="scope-name">${scope.scope}</div>
                      <div class="scope-count">${scope.count} items</div>
                      <div class="scope-percentage">${scope.percentage}%</div>
                    </div>
                  `).join('')}
                </div>
                <p>Your data covers ${scopes.length} emission scope${scopes.length > 1 ? 's' : ''}, with <strong>${scopeCounts[0].scope}</strong> being the most represented (${scopeCounts[0].count} items, ${scopeCounts[0].percentage}%).</p>
                ${scopes.length < 3 ? '<p class="recommendation">💡 <strong>Recommendation:</strong> Consider expanding scope coverage to include all three scopes (Scope 1, 2, and 3) for comprehensive emissions reporting.</p>' : ''}
              </div>
            </div>
          `);
        }

        // Status Analysis Section
        if (statuses.length > 0) {
          const statusCounts = statuses.map(status => ({
            status,
            count: data.filter(item => item.validation_status === status).length,
            percentage: Math.round(data.filter(item => item.validation_status === status).length / totalItems * 100)
          })).sort((a, b) => b.count - a.count);

          insights.push(`
            <div class="insight-section">
              <h3>✅ Validation Status Analysis</h3>
              <div class="insight-card">
                <div class="status-grid">
                  ${statusCounts.map(status => `
                    <div class="status-item status-${status.status.toLowerCase().replace(/\s+/g, '-')}">
                      <div class="status-name">${status.status}</div>
                      <div class="status-count">${status.count}</div>
                      <div class="status-percentage">${status.percentage}%</div>
                    </div>
                  `).join('')}
                </div>
                <p>Most items have status: <strong>${statusCounts[0].status}</strong> (${statusCounts[0].count} items, ${statusCounts[0].percentage}%).</p>
                ${statusCounts.find(s => s.status.toLowerCase().includes('pending')) ? '<p class="recommendation">⚠️ <strong>Action Required:</strong> Review and validate pending data items to ensure data quality and completeness.</p>' : ''}
              </div>
            </div>
          `);
        }

        // Recent Activity Section
        if (recentItems.length > 0) {
          insights.push(`
            <div class="insight-section">
              <h3>🕒 Recent Activity</h3>
              <div class="insight-card">
                <div class="activity-metric">
                  <div class="activity-value">${recentItems.length}</div>
                  <div class="activity-label">Items added in last 30 days</div>
                </div>
                <p>Your organization has been actively adding data, with <strong>${recentItems.length}</strong> new items in the past 30 days (${Math.round(recentItems.length / totalItems * 100)}% of total dataset).</p>
              </div>
            </div>
          `);
        }

        // Performance Metrics Section
        if (avgValue > 0) {
          insights.push(`
            <div class="insight-section">
              <h3>📊 Performance Metrics</h3>
              <div class="insight-card">
                <div class="performance-metric">
                  <div class="metric-value">${avgValue.toFixed(2)}</div>
                  <div class="metric-label">Average Value</div>
                </div>
                <p>The average value across all data points is <strong>${avgValue.toFixed(2)}</strong>, providing a baseline for performance comparison and trend analysis.</p>
              </div>
            </div>
          `);
        }

        // Recommendations Section
        insights.push(`
          <div class="insight-section">
            <h3>💡 Strategic Recommendations</h3>
            <div class="insight-card">
              <div class="recommendations-list">
                ${metrics.length === 0 ? '<div class="recommendation-item">➕ <strong>Add ESG Metrics:</strong> Consider adding ESG metrics to track environmental, social, and governance performance across your organization.</div>' : ''}
                ${materials.length === 0 ? '<div class="recommendation-item">🏭 <strong>Include Materials Data:</strong> Add materials data to track supply chain sustainability and identify opportunities for improvement.</div>' : ''}
                ${statuses.includes('pending') ? '<div class="recommendation-item">✅ <strong>Validate Pending Data:</strong> Review and validate pending data items to ensure data quality and regulatory compliance.</div>' : ''}
                ${scopes.length < 3 ? '<div class="recommendation-item">🌍 <strong>Expand Scope Coverage:</strong> Include Scope 1, 2, and 3 emissions for comprehensive sustainability reporting.</div>' : ''}
                ${recentItems.length < totalItems * 0.1 ? '<div class="recommendation-item">📈 <strong>Increase Data Collection:</strong> Consider more frequent data collection to improve real-time monitoring and decision-making.</div>' : ''}
                <div class="recommendation-item">📋 <strong>Regular Reviews:</strong> Schedule regular data quality reviews and updates to maintain accuracy and relevance.</div>
                <div class="recommendation-item">🎯 <strong>Set Targets:</strong> Establish clear sustainability targets based on your current data to drive continuous improvement.</div>
              </div>
            </div>
          </div>
        `);

        return insights.join('');
      };

      // Create HTML content
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Organizational Data Overview Report</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
              color: #333;
            }
            .container {
              max-width: 1200px;
              margin: 0 auto;
              background: white;
              border-radius: 15px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.1);
              overflow: hidden;
            }
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 40px;
              text-align: center;
            }
            .header h1 {
              margin: 0;
              font-size: 2.5em;
              font-weight: 300;
              letter-spacing: 1px;
            }
            .header-info {
              display: flex;
              justify-content: space-around;
              margin-top: 30px;
              flex-wrap: wrap;
            }
            .header-item {
              text-align: center;
              padding: 15px;
            }
            .header-value {
              font-size: 2em;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .header-label {
              font-size: 0.9em;
              opacity: 0.9;
            }
            .content {
              padding: 40px;
            }
            .insight-section {
              margin-bottom: 40px;
            }
            .insight-section h3 {
              color: #667eea;
              font-size: 1.5em;
              margin-bottom: 20px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 10px;
            }
            .insight-card {
              background: #f8f9fa;
              border-radius: 10px;
              padding: 25px;
              border-left: 5px solid #667eea;
              box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            }
            .metric-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 20px;
              margin-bottom: 20px;
            }
            .metric-item {
              text-align: center;
              padding: 20px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .metric-value {
              font-size: 2em;
              font-weight: bold;
              color: #667eea;
              margin-bottom: 5px;
            }
            .metric-label {
              font-size: 0.9em;
              color: #666;
            }
            .distribution-chart {
              margin: 20px 0;
            }
            .chart-bar {
              display: flex;
              align-items: center;
              margin-bottom: 15px;
              gap: 15px;
            }
            .bar-label {
              width: 120px;
              font-weight: 500;
            }
            .bar-container {
              flex: 1;
              height: 25px;
              background: #e9ecef;
              border-radius: 12px;
              overflow: hidden;
            }
            .bar-fill {
              height: 100%;
              background: linear-gradient(90deg, #667eea, #764ba2);
              border-radius: 12px;
              transition: width 0.3s ease;
            }
            .bar-value {
              width: 80px;
              text-align: right;
              font-weight: 500;
            }
            .category-table, .scope-grid, .status-grid {
              margin: 20px 0;
            }
            .table-header {
              display: grid;
              grid-template-columns: 2fr 1fr 1fr;
              background: #667eea;
              color: white;
              padding: 12px;
              border-radius: 8px 8px 0 0;
              font-weight: 500;
            }
            .table-row {
              display: grid;
              grid-template-columns: 2fr 1fr 1fr;
              padding: 12px;
              border-bottom: 1px solid #e9ecef;
              background: white;
            }
            .table-row:nth-child(even) {
              background: #f8f9fa;
            }
            .table-row:last-child {
              border-radius: 0 0 8px 8px;
            }
            .scope-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
            }
            .scope-item {
              background: white;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .scope-name {
              font-weight: 500;
              color: #667eea;
              margin-bottom: 10px;
            }
            .scope-count {
              font-size: 1.5em;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .scope-percentage {
              color: #666;
              font-size: 0.9em;
            }
            .status-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
              gap: 15px;
            }
            .status-item {
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .status-approved {
              background: linear-gradient(135deg, #d4edda, #c3e6cb);
              border-left: 5px solid #28a745;
            }
            .status-pending {
              background: linear-gradient(135deg, #fff3cd, #ffeaa7);
              border-left: 5px solid #ffc107;
            }
            .status-rejected {
              background: linear-gradient(135deg, #f8d7da, #f5c6cb);
              border-left: 5px solid #dc3545;
            }
            .status-name {
              font-weight: 500;
              margin-bottom: 10px;
            }
            .status-count {
              font-size: 1.5em;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .status-percentage {
              color: #666;
              font-size: 0.9em;
            }
            .activity-metric, .performance-metric {
              text-align: center;
              padding: 30px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              margin: 20px 0;
            }
            .activity-value, .performance-metric .metric-value {
              font-size: 3em;
              font-weight: bold;
              color: #667eea;
              margin-bottom: 10px;
            }
            .activity-label, .performance-metric .metric-label {
              font-size: 1.1em;
              color: #666;
            }
            .recommendations-list {
              display: grid;
              gap: 15px;
            }
            .recommendation-item {
              padding: 15px;
              background: white;
              border-radius: 8px;
              border-left: 4px solid #667eea;
              box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            }
            .recommendation {
              background: #fff3cd;
              border: 1px solid #ffeaa7;
              border-radius: 8px;
              padding: 15px;
              margin-top: 15px;
            }
            .data-table {
              margin-top: 40px;
            }
            .data-table h2 {
              color: #667eea;
              font-size: 1.8em;
              margin-bottom: 20px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            th {
              background: #667eea;
              color: white;
              padding: 15px;
              text-align: left;
              font-weight: 500;
            }
            td {
              padding: 12px 15px;
              border-bottom: 1px solid #e9ecef;
            }
            tr:nth-child(even) {
              background: #f8f9fa;
            }
            tr:hover {
              background: #e3f2fd;
            }
            @media (max-width: 768px) {
              .header-info {
                flex-direction: column;
              }
              .metric-grid {
                grid-template-columns: repeat(2, 1fr);
              }
              .scope-grid, .status-grid {
                grid-template-columns: 1fr;
              }
              .table-header, .table-row {
                grid-template-columns: 1fr;
                text-align: center;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Organizational Data Overview Report</h1>
              <div class="header-info">
                <div class="header-item">
                  <div class="header-value">${data.length}</div>
                  <div class="header-label">Total Items</div>
                </div>
                <div class="header-item">
                  <div class="header-value">${metrics.length}</div>
                  <div class="header-label">ESG Metrics</div>
                </div>
                <div class="header-item">
                  <div class="header-value">${materials.length}</div>
                  <div class="header-label">Materials</div>
                </div>
                <div class="header-item">
                  <div class="header-value">${new Date().toLocaleDateString()}</div>
                  <div class="header-label">Generated Date</div>
                </div>
              </div>
            </div>
            
            <div class="content">
              ${generateAIInsights()}
              
              <div class="data-table">
                <h2>📋 Complete Data Table</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Value</th>
                      <th>Unit</th>
                      <th>Scope</th>
                      <th>Status</th>
                      <th>Reporting Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${data.map(item => `
                      <tr>
                        <td>${item.data_type || ''}</td>
                        <td>${item.name || ''}</td>
                        <td>${item.category || ''}</td>
                        <td>${item.custom_value || ''}</td>
                        <td>${item.unit || ''}</td>
                        <td>${item.scope || ''}</td>
                        <td>${item.validation_status || 'pending'}</td>
                        <td>${item.reporting_period || 'N/A'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      // Create and download file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `organizational_data_overview_${new Date().toISOString().split('T')[0]}.html`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Export Successful',
        description: 'Organizational data has been exported to HTML successfully.',
      });
    } catch (error) {
      console.error('Error exporting HTML:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export organizational data to HTML.',
        variant: 'destructive'
      });
    }
  };

  const handleExportPDF = async () => {
    if (!(await guardDownload())) return;

    try {
      // Calculate metrics and materials counts
      const metrics = data.filter(item => item.data_type === 'metric');
      const materials = data.filter(item => item.data_type === 'material');

      // Generate AI insights based on the current filtered data
      const generateAIInsights = () => {
        if (data.length === 0) {
          return `
            <div class="insight-section">
              <h3>📊 Dataset Overview</h3>
              <div class="insight-card">
                <p>No organizational data available for analysis. Consider adding ESG metrics and materials data to begin tracking your sustainability performance.</p>
              </div>
            </div>
          `;
        }

        const insights = [];

        // Basic statistics
        const totalItems = data.length;
        const categories = [...new Set(data.map(item => item.category).filter(Boolean))];
        const scopes = [...new Set(data.map(item => item.scope).filter(Boolean))];
        const statuses = [...new Set(data.map(item => item.validation_status).filter(Boolean))];

        // Calculate additional metrics
        const avgValue = data.reduce((sum, item) => sum + (item.custom_value || 0), 0) / data.length;
        const recentItems = data.filter(item => {
          const createdDate = new Date(item.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return createdDate > thirtyDaysAgo;
        });

        // Dataset Overview Section
        insights.push(`
          <div class="insight-section">
            <h3>📊 Dataset Overview</h3>
            <div class="insight-card">
              <div class="metric-grid">
                <div class="metric-item">
                  <div class="metric-value">${totalItems}</div>
                  <div class="metric-label">Total Items</div>
                </div>
                <div class="metric-item">
                  <div class="metric-value">${metrics.length}</div>
                  <div class="metric-label">ESG Metrics</div>
                </div>
                <div class="metric-item">
                  <div class="metric-value">${materials.length}</div>
                  <div class="metric-label">Materials</div>
                </div>
                <div class="metric-item">
                  <div class="metric-value">${categories.length}</div>
                  <div class="metric-label">Categories</div>
                </div>
              </div>
              <p>This comprehensive dataset spans <strong>${scopes.length}</strong> emission scopes with <strong>${statuses.length}</strong> different validation statuses, providing a holistic view of your organization's sustainability performance.</p>
            </div>
          </div>
        `);

        // Data Type Distribution Section
        insights.push(`
          <div class="insight-section">
            <h3>📈 Data Type Distribution</h3>
            <div class="insight-card">
              <div class="distribution-chart">
                <div class="chart-bar">
                  <div class="bar-label">ESG Metrics</div>
                  <div class="bar-container">
                    <div class="bar-fill" style="width: ${Math.round(metrics.length / totalItems * 100)}%"></div>
                  </div>
                  <div class="bar-value">${metrics.length} (${Math.round(metrics.length / totalItems * 100)}%)</div>
                </div>
                <div class="chart-bar">
                  <div class="bar-label">Materials</div>
                  <div class="bar-container">
                    <div class="bar-fill" style="width: ${Math.round(materials.length / totalItems * 100)}%"></div>
                  </div>
                  <div class="bar-value">${materials.length} (${Math.round(materials.length / totalItems * 100)}%)</div>
                </div>
              </div>
              <p>The dataset contains <strong>${metrics.length}</strong> ESG metrics and <strong>${materials.length}</strong> materials, providing balanced coverage of both performance indicators and supply chain data.</p>
            </div>
          </div>
        `);

        // Category Analysis Section
        if (categories.length > 0) {
          const categoryCounts = categories.map(cat => ({
            category: cat,
            count: data.filter(item => item.category === cat).length,
            percentage: Math.round(data.filter(item => item.category === cat).length / totalItems * 100)
          })).sort((a, b) => b.count - a.count);

          insights.push(`
            <div class="insight-section">
              <h3>🏷️ Category Analysis</h3>
              <div class="insight-card">
                <div class="category-table">
                  <div class="table-header">
                    <div class="header-cell">Category</div>
                    <div class="header-cell">Count</div>
                    <div class="header-cell">Percentage</div>
                  </div>
                  ${categoryCounts.map(cat => `
                    <div class="table-row">
                      <div class="table-cell">${cat.category}</div>
                      <div class="table-cell">${cat.count}</div>
                      <div class="table-cell">${cat.percentage}%</div>
                    </div>
                  `).join('')}
                </div>
                <p>The most represented category is <strong>${categoryCounts[0].category}</strong> with ${categoryCounts[0].count} items (${categoryCounts[0].percentage}% of total). This indicates a strong focus on ${categoryCounts[0].category.toLowerCase()} in your sustainability strategy.</p>
              </div>
            </div>
          `);
        }

        // Scope Analysis Section
        if (scopes.length > 0) {
          const scopeCounts = scopes.map(scope => ({
            scope,
            count: data.filter(item => item.scope === scope).length,
            percentage: Math.round(data.filter(item => item.scope === scope).length / totalItems * 100)
          })).sort((a, b) => b.count - a.count);

          insights.push(`
            <div class="insight-section">
              <h3>🌍 Emission Scope Coverage</h3>
              <div class="insight-card">
                <div class="scope-grid">
                  ${scopeCounts.map(scope => `
                    <div class="scope-item">
                      <div class="scope-name">${scope.scope}</div>
                      <div class="scope-count">${scope.count} items</div>
                      <div class="scope-percentage">${scope.percentage}%</div>
                    </div>
                  `).join('')}
                </div>
                <p>Your data covers ${scopes.length} emission scope${scopes.length > 1 ? 's' : ''}, with <strong>${scopeCounts[0].scope}</strong> being the most represented (${scopeCounts[0].count} items, ${scopeCounts[0].percentage}%).</p>
                ${scopes.length < 3 ? '<p class="recommendation">💡 <strong>Recommendation:</strong> Consider expanding scope coverage to include all three scopes (Scope 1, 2, and 3) for comprehensive emissions reporting.</p>' : ''}
              </div>
            </div>
          `);
        }

        // Status Analysis Section
        if (statuses.length > 0) {
          const statusCounts = statuses.map(status => ({
            status,
            count: data.filter(item => item.validation_status === status).length,
            percentage: Math.round(data.filter(item => item.validation_status === status).length / totalItems * 100)
          })).sort((a, b) => b.count - a.count);

          insights.push(`
            <div class="insight-section">
              <h3>✅ Validation Status Analysis</h3>
              <div class="insight-card">
                <div class="status-grid">
                  ${statusCounts.map(status => `
                    <div class="status-item status-${status.status.toLowerCase().replace(/\s+/g, '-')}">
                      <div class="status-name">${status.status}</div>
                      <div class="status-count">${status.count}</div>
                      <div class="status-percentage">${status.percentage}%</div>
                    </div>
                  `).join('')}
                </div>
                <p>Most items have status: <strong>${statusCounts[0].status}</strong> (${statusCounts[0].count} items, ${statusCounts[0].percentage}%).</p>
                ${statusCounts.find(s => s.status.toLowerCase().includes('pending')) ? '<p class="recommendation">⚠️ <strong>Action Required:</strong> Review and validate pending data items to ensure data quality and completeness.</p>' : ''}
              </div>
            </div>
          `);
        }

        // Recent Activity Section
        if (recentItems.length > 0) {
          insights.push(`
            <div class="insight-section">
              <h3>🕒 Recent Activity</h3>
              <div class="insight-card">
                <div class="activity-metric">
                  <div class="activity-value">${recentItems.length}</div>
                  <div class="activity-label">Items added in last 30 days</div>
                </div>
                <p>Your organization has been actively adding data, with <strong>${recentItems.length}</strong> new items in the past 30 days (${Math.round(recentItems.length / totalItems * 100)}% of total dataset).</p>
              </div>
            </div>
          `);
        }

        // Performance Metrics Section
        if (avgValue > 0) {
          insights.push(`
            <div class="insight-section">
              <h3>📊 Performance Metrics</h3>
              <div class="insight-card">
                <div class="performance-metric">
                  <div class="metric-value">${avgValue.toFixed(2)}</div>
                  <div class="metric-label">Average Value</div>
                </div>
                <p>The average value across all data points is <strong>${avgValue.toFixed(2)}</strong>, providing a baseline for performance comparison and trend analysis.</p>
              </div>
            </div>
          `);
        }

        // Recommendations Section
        insights.push(`
          <div class="insight-section">
            <h3>💡 Strategic Recommendations</h3>
            <div class="insight-card">
              <div class="recommendations-list">
                ${metrics.length === 0 ? '<div class="recommendation-item">➕ <strong>Add ESG Metrics:</strong> Consider adding ESG metrics to track environmental, social, and governance performance across your organization.</div>' : ''}
                ${materials.length === 0 ? '<div class="recommendation-item">🏭 <strong>Include Materials Data:</strong> Add materials data to track supply chain sustainability and identify opportunities for improvement.</div>' : ''}
                ${statuses.includes('pending') ? '<div class="recommendation-item">✅ <strong>Validate Pending Data:</strong> Review and validate pending data items to ensure data quality and regulatory compliance.</div>' : ''}
                ${scopes.length < 3 ? '<div class="recommendation-item">🌍 <strong>Expand Scope Coverage:</strong> Include Scope 1, 2, and 3 emissions for comprehensive sustainability reporting.</div>' : ''}
                ${recentItems.length < totalItems * 0.1 ? '<div class="recommendation-item">📈 <strong>Increase Data Collection:</strong> Consider more frequent data collection to improve real-time monitoring and decision-making.</div>' : ''}
                <div class="recommendation-item">📋 <strong>Regular Reviews:</strong> Schedule regular data quality reviews and updates to maintain accuracy and relevance.</div>
                <div class="recommendation-item">🎯 <strong>Set Targets:</strong> Establish clear sustainability targets based on your current data to drive continuous improvement.</div>
              </div>
            </div>
          </div>
        `);

        return insights.join('');
      };

      // Create HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Organizational Data Overview Report</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
              color: #333;
            }
            .container {
              max-width: 1200px;
              margin: 0 auto;
              background: white;
              border-radius: 15px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.1);
              overflow: hidden;
            }
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 40px;
              text-align: center;
            }
            .header h1 {
              margin: 0;
              font-size: 2.5em;
              font-weight: 300;
              letter-spacing: 1px;
            }
            .header-info {
              display: flex;
              justify-content: space-around;
              margin-top: 30px;
              flex-wrap: wrap;
            }
            .header-item {
              text-align: center;
              padding: 15px;
            }
            .header-value {
              font-size: 2em;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .header-label {
              font-size: 0.9em;
              opacity: 0.9;
            }
            .content {
              padding: 40px;
            }
            .insight-section {
              margin-bottom: 40px;
            }
            .insight-section h3 {
              color: #667eea;
              font-size: 1.5em;
              margin-bottom: 20px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 10px;
            }
            .insight-card {
              background: #f8f9fa;
              border-radius: 10px;
              padding: 25px;
              border-left: 5px solid #667eea;
              box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            }
            .metric-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 20px;
              margin-bottom: 20px;
            }
            .metric-item {
              text-align: center;
              padding: 20px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .metric-value {
              font-size: 2em;
              font-weight: bold;
              color: #667eea;
              margin-bottom: 5px;
            }
            .metric-label {
              font-size: 0.9em;
              color: #666;
            }
            .distribution-chart {
              margin: 20px 0;
            }
            .chart-bar {
              display: flex;
              align-items: center;
              margin-bottom: 15px;
              gap: 15px;
            }
            .bar-label {
              width: 120px;
              font-weight: 500;
            }
            .bar-container {
              flex: 1;
              height: 25px;
              background: #e9ecef;
              border-radius: 12px;
              overflow: hidden;
            }
            .bar-fill {
              height: 100%;
              background: linear-gradient(90deg, #667eea, #764ba2);
              border-radius: 12px;
              transition: width 0.3s ease;
            }
            .bar-value {
              width: 80px;
              text-align: right;
              font-weight: 500;
            }
            .category-table, .scope-grid, .status-grid {
              margin: 20px 0;
            }
            .table-header {
              display: grid;
              grid-template-columns: 2fr 1fr 1fr;
              background: #667eea;
              color: white;
              padding: 12px;
              border-radius: 8px 8px 0 0;
              font-weight: 500;
            }
            .table-row {
              display: grid;
              grid-template-columns: 2fr 1fr 1fr;
              padding: 12px;
              border-bottom: 1px solid #e9ecef;
              background: white;
            }
            .table-row:nth-child(even) {
              background: #f8f9fa;
            }
            .table-row:last-child {
              border-radius: 0 0 8px 8px;
            }
            .scope-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
            }
            .scope-item {
              background: white;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .scope-name {
              font-weight: 500;
              color: #667eea;
              margin-bottom: 10px;
            }
            .scope-count {
              font-size: 1.5em;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .scope-percentage {
              color: #666;
              font-size: 0.9em;
            }
            .status-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
              gap: 15px;
            }
            .status-item {
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .status-approved {
              background: linear-gradient(135deg, #d4edda, #c3e6cb);
              border-left: 5px solid #28a745;
            }
            .status-pending {
              background: linear-gradient(135deg, #fff3cd, #ffeaa7);
              border-left: 5px solid #ffc107;
            }
            .status-rejected {
              background: linear-gradient(135deg, #f8d7da, #f5c6cb);
              border-left: 5px solid #dc3545;
            }
            .status-name {
              font-weight: 500;
              margin-bottom: 10px;
            }
            .status-count {
              font-size: 1.5em;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .status-percentage {
              color: #666;
              font-size: 0.9em;
            }
            .activity-metric, .performance-metric {
              text-align: center;
              padding: 30px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              margin: 20px 0;
            }
            .activity-value, .performance-metric .metric-value {
              font-size: 3em;
              font-weight: bold;
              color: #667eea;
              margin-bottom: 10px;
            }
            .activity-label, .performance-metric .metric-label {
              font-size: 1.1em;
              color: #666;
            }
            .recommendations-list {
              display: grid;
              gap: 15px;
            }
            .recommendation-item {
              padding: 15px;
              background: white;
              border-radius: 8px;
              border-left: 4px solid #667eea;
              box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            }
            .recommendation {
              background: #fff3cd;
              border: 1px solid #ffeaa7;
              border-radius: 8px;
              padding: 15px;
              margin-top: 15px;
            }
            .data-table {
              margin-top: 40px;
            }
            .data-table h2 {
              color: #667eea;
              font-size: 1.8em;
              margin-bottom: 20px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            th {
              background: #667eea;
              color: white;
              padding: 15px;
              text-align: left;
              font-weight: 500;
            }
            td {
              padding: 12px 15px;
              border-bottom: 1px solid #e9ecef;
            }
            tr:nth-child(even) {
              background: #f8f9fa;
            }
            tr:hover {
              background: #e3f2fd;
            }
            @media (max-width: 768px) {
              .header-info {
                flex-direction: column;
              }
              .metric-grid {
                grid-template-columns: repeat(2, 1fr);
              }
              .scope-grid, .status-grid {
                grid-template-columns: 1fr;
              }
              .table-header, .table-row {
                grid-template-columns: 1fr;
                text-align: center;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Organizational Data Overview Report</h1>
              <div class="header-info">
                <div class="header-item">
                  <div class="header-value">${data.length}</div>
                  <div class="header-label">Total Items</div>
                </div>
                <div class="header-item">
                  <div class="header-value">${metrics.length}</div>
                  <div class="header-label">ESG Metrics</div>
                </div>
                <div class="header-item">
                  <div class="header-value">${materials.length}</div>
                  <div class="header-label">Materials</div>
                </div>
                <div class="header-item">
                  <div class="header-value">${new Date().toLocaleDateString()}</div>
                  <div class="header-label">Generated Date</div>
                </div>
              </div>
            </div>
            
            <div class="content">
              ${generateAIInsights()}
              
              <div class="data-table">
                <h2>📋 Complete Data Table</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Value</th>
                      <th>Unit</th>
                      <th>Scope</th>
                      <th>Status</th>
                      <th>Reporting Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${data.map(item => `
                      <tr>
                        <td>${item.data_type || ''}</td>
                        <td>${item.name || ''}</td>
                        <td>${item.category || ''}</td>
                        <td>${item.custom_value || ''}</td>
                        <td>${item.unit || ''}</td>
                        <td>${item.scope || ''}</td>
                        <td>${item.validation_status || 'pending'}</td>
                        <td>${item.reporting_period || 'N/A'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      // Convert HTML to PDF using jsPDF and html2canvas
      const { jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');

      // Create a temporary div to render the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '1200px';
      document.body.appendChild(tempDiv);

      try {
        const canvas = await html2canvas.default(tempDiv, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          width: 1200,
          height: tempDiv.scrollHeight
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');

        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // Save the PDF
        pdf.save(`organizational_data_overview_${new Date().toISOString().split('T')[0]}.pdf`);

        toast({
          title: 'Export Successful',
          description: 'Organizational data has been exported to PDF successfully.',
        });
      } finally {
        document.body.removeChild(tempDiv);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export organizational data to PDF.',
        variant: 'destructive'
      });
    }
  };

  // Filter data based on search and filters
  const filteredData = data.filter(item => {
    const matchesSearch = (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (item.data_source?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesScope = filterScope === 'all' || item.scope === filterScope;
    const matchesStatus = filterStatus === 'all' || item.validation_status === filterStatus;
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesDataType = filterDataType === 'all' || item.data_type === filterDataType;

    return matchesSearch && matchesScope && matchesStatus && matchesCategory && matchesDataType;
  });

  // Get unique categories, scopes, and statuses from data
  const categories = [...new Set(data.map(item => item.category).filter(Boolean))];
  const scopes = [...new Set(data.map(item => item.scope).filter(Boolean))];
  const statuses = [...new Set(data.map(item => item.validation_status).filter(Boolean))];

  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>All Metrics & Materials Data</span>
              {user && (
                <Badge variant="secondary" className={
                  'bg-green-100 text-green-800'
                }>
                  Individual User
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onTabChange?.('quick-entry')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Data
              </Button>
              <Button variant="outline" size="sm" onClick={() => onTabChange?.('metrics')}>
                <Library className="h-4 w-4 mr-2" />
                Browse Metrics
              </Button>
              <Button variant="outline" size="sm" onClick={handleManualRefresh} title="Refresh Data">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardTitle>

          {/* Export Section */}
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Export Data:</span>
              <Button variant="outline" size="sm" onClick={handleExportCSV} className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100">
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportHTML} className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100">
                <Download className="h-4 w-4 mr-2" />
                HTML
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100">
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            </div>

            {/* Admin Status Display */}
            {!checkingAdminStatus && (
              <div className="flex items-center gap-2">
                {isAdmin ? (
                  <Badge variant="default" className="bg-purple-100 text-purple-800">
                    {userType === 'super_admin' ? 'Super Admin' : 'Organization Admin'}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                    Individual User
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Metric Name</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reporting Period</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => {
                  const reportingPeriod = item.reporting_period || item.custom_metadata?.reporting_period || '';

                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Badge variant={item.data_type === 'metric' ? 'default' : 'secondary'}>
                          {item.data_type === 'metric' ? 'Metric' : 'Material'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{item.name || 'N/A'}</TableCell>
                      <TableCell>{item.custom_value || 'N/A'} {item.unit || 'N/A'}</TableCell>
                      <TableCell>{item.category || 'N/A'}</TableCell>
                      <TableCell>{item.scope || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(item.validation_status || item.custom_metadata?.validation_status || 'pending')}>
                          {item.validation_status || item.custom_metadata?.validation_status || 'pending'}
                        </Badge>
                      </TableCell>
                      <TableCell>{reportingPeriod || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewData(item)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditData(item)}
                            title="Edit Data"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {isAdmin && (item.validation_status === 'pending' || item.custom_metadata?.validation_status === 'pending') && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApproveData(item)}
                                title="Approve Data"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectData(item)}
                                title="Reject Data"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteData(item.id)}
                            title="Delete Data"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Empty State */}
            {filteredData.length === 0 && (
              <div className="text-center py-12">
                <Database className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || filterCategory !== 'all' || filterScope !== 'all' || filterStatus !== 'all' || filterDataType !== 'all'
                    ? 'No data matches your current filters. Try adjusting your search criteria.'
                    : 'You haven\'t added any metrics or materials yet. Start by adding data through the Quick Entry tab or by importing from the Metrics/Materials libraries.'
                  }
                </p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => onTabChange?.('quick-entry')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Data
                  </Button>
                  <Button variant="outline" onClick={() => onTabChange?.('metrics')}>
                    <Library className="h-4 w-4 mr-2" />
                    Browse Metrics
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedDataPoint && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  {selectedDataPoint.name || 'Data Point Details'}
                </DialogTitle>
                <DialogDescription>
                  Comprehensive view of all data point information
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-blue-800">Data Point Name</Label>
                      <p className="text-sm font-semibold text-blue-900 mt-1">{selectedDataPoint.name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-blue-800">Data Type</Label>
                      <Badge variant="outline" className="mt-1">
                        {selectedDataPoint.data_type === 'metric' ? 'ESG Metric' : 'Material'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-blue-800">Category</Label>
                      <p className="text-sm text-blue-900 mt-1">{selectedDataPoint.category || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-blue-800">Subcategory</Label>
                      <p className="text-sm text-blue-900 mt-1">{selectedDataPoint.subcategory || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-blue-800">Value</Label>
                      <p className="text-sm font-semibold text-blue-900 mt-1">
                        {selectedDataPoint.custom_value || 'N/A'} {selectedDataPoint.unit || ''}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-blue-800">Unit</Label>
                      <p className="text-sm text-blue-900 mt-1">{selectedDataPoint.unit || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* ESG Classification Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    ESG Classification
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-green-800">Emission Scope</Label>
                      {selectedDataPoint.scope ? (
                        <Badge className={`mt-1 ${getScopeColor(selectedDataPoint.scope)}`}>
                          {selectedDataPoint.scope}
                        </Badge>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">N/A</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-green-800">Validation Status</Label>
                      <Badge className={`mt-1 ${getStatusColor(selectedDataPoint.validation_status || 'pending')}`}>
                        {selectedDataPoint.validation_status || 'pending'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-green-800">Data Source</Label>
                      <p className="text-sm text-green-900 mt-1">{selectedDataPoint.data_source || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-green-800">Reporting Period</Label>
                      <p className="text-sm text-green-900 mt-1">{selectedDataPoint.reporting_period || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-green-800">Calculation Method</Label>
                      <p className="text-sm text-green-900 mt-1">
                        {selectedDataPoint.custom_metadata?.calculation_method || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-green-800">Verification Status</Label>
                      <p className="text-sm text-green-900 mt-1">
                        {selectedDataPoint.custom_metadata?.verification_status || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Additional Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-purple-800">Notes</Label>
                      <p className="text-sm text-purple-900 mt-1 whitespace-pre-wrap">
                        {selectedDataPoint.custom_metadata?.notes || 'No additional notes available'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-purple-800">Tags</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedDataPoint.custom_metadata?.tags && selectedDataPoint.custom_metadata.tags.length > 0 ? (
                          selectedDataPoint.custom_metadata.tags.map((tag: string, index: number) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">No tags assigned</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Information Section */}
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    System Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Data Point ID</Label>
                      <p className="text-sm text-gray-900 font-mono mt-1">{selectedDataPoint.id || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Created At</Label>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedDataPoint.created_at ? new Date(selectedDataPoint.created_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Last Updated</Label>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedDataPoint.custom_metadata?.updated_at ?
                          new Date(selectedDataPoint.custom_metadata.updated_at).toLocaleString() :
                          'N/A'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedDataPoint && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit {selectedDataPoint.name || 'Data Point'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-blue-800">Name</Label>
                      <Input
                        id="name"
                        value={selectedDataPoint.name || ''}
                        disabled
                        className="bg-white border-blue-300"
                      />
                      <p className="text-xs text-blue-600 mt-1">Name cannot be changed</p>
                    </div>
                    <div>
                      <Label htmlFor="data_type" className="text-sm font-medium text-blue-800">Data Type</Label>
                      <Input
                        id="data_type"
                        value={selectedDataPoint.data_type === 'metric' ? 'ESG Metric' : 'Material'}
                        disabled
                        className="bg-white border-blue-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category" className="text-sm font-medium text-blue-800">Category</Label>
                      <Input
                        id="category"
                        value={selectedDataPoint.category || ''}
                        disabled
                        className="bg-white border-blue-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subcategory" className="text-sm font-medium text-blue-800">Subcategory</Label>
                      <Input
                        id="subcategory"
                        value={selectedDataPoint.subcategory || ''}
                        disabled
                        className="bg-white border-blue-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Values and Units Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Values and Units
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="metric_value" className="text-sm font-medium text-green-800">
                        {selectedDataPoint.data_type === 'metric' ? 'Metric Value' : 'Material Density'}
                      </Label>
                      <Input
                        id="metric_value"
                        type="number"
                        step="0.01"
                        value={editFormData.metric_value}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, metric_value: e.target.value }))}
                        className="bg-white border-green-300 focus:border-green-500"
                        placeholder="Enter value"
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit" className="text-sm font-medium text-green-800">Unit</Label>
                      <Input
                        id="unit"
                        value={selectedDataPoint.unit || ''}
                        disabled
                        className="bg-white border-green-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Reporting and Scope Section */}
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Reporting and Scope
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reporting_period" className="text-sm font-medium text-purple-800">Reporting Period</Label>
                      <Input
                        id="reporting_period"
                        type="date"
                        value={editFormData.reporting_period}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, reporting_period: e.target.value }))}
                        className="bg-white border-purple-300 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="scope" className="text-sm font-medium text-purple-800">Scope</Label>
                      <Select
                        value={editFormData.scope}
                        onValueChange={(value) => setEditFormData(prev => ({ ...prev, scope: value }))}
                      >
                        <SelectTrigger className="bg-white border-purple-300 focus:border-purple-500">
                          <SelectValue placeholder="Select scope" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Scope 1">Scope 1</SelectItem>
                          <SelectItem value="Scope 2">Scope 2</SelectItem>
                          <SelectItem value="Scope 3">Scope 3</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="data_source" className="text-sm font-medium text-purple-800">Data Source</Label>
                      <Input
                        id="data_source"
                        value={editFormData.data_source}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, data_source: e.target.value }))}
                        className="bg-white border-purple-300 focus:border-purple-500"
                        placeholder="e.g., Utility bills, Supplier data, etc."
                      />
                    </div>
                    <div>
                      <Label htmlFor="validation_status" className="text-sm font-medium text-purple-800">Validation Status</Label>
                      <Select
                        value={selectedDataPoint.validation_status || selectedDataPoint.custom_metadata?.validation_status || 'pending'}
                        disabled={!isAdmin}
                      >
                        <SelectTrigger className="bg-white border-purple-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      {!isAdmin && (
                        <p className="text-xs text-purple-600 mt-1">Only admins can change validation status</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                  <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Additional Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="notes" className="text-sm font-medium text-orange-800">
                        Notes
                      </Label>
                      <Textarea
                        id="notes"
                        placeholder="Enter additional notes or context about this data point..."
                        value={editFormData.notes || ''}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="bg-white border-orange-300 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditModalOpen(false)}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this data point? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setIdToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
